import { useLocalization } from '@components/Localization'
import { Box } from '@gassan-ui'
import { Button, Heading } from '@rolex-ui'
import { useBodyScrollLock } from '@lib/hooks/useBodyScrollLock'
import React, { FC, useEffect, useRef } from 'react'
import * as S from './elements'
import { rem } from 'polished'

interface Props {
  onConfirm: () => void
  onReset: () => void
  onOpen: () => void
  onClose: () => void
  isOpen: boolean
  disableReset: boolean
}

const Sidebar: FC<Props> = ({
  children,
  onConfirm,
  onReset,
  onOpen,
  onClose,
  isOpen,
  disableReset
}) => {
  const { messages } = useLocalization()
  const ref = useRef<HTMLDivElement>()

  useBodyScrollLock(ref, isOpen)

  useEffect(() => {
    // Always scroll content to top if sidebar is opened
    if (isOpen && ref.current) {
      ref.current.scrollTo({ top: 0, left: 0 })
    }
  }, [isOpen]) // eslint-disable-line

  return (
    <>
      <Box
        display={{ _: 'block', large: 'none' }}
        position={{ _: 'sticky', large: 'relative' }}
        mr={{ _: 0, small: '5%', large: 0 }}
        top={[0, 'auto']}
        zIndex={3}
      >
        <S.FullWidthButton onClick={onOpen} isOpen={isOpen}>
          Filters
          <svg height="11" width="11" viewBox="0 0 11 11" fill="currentColor">
            <path d="m11 6.15h-4.85v4.85h-1.3v-4.85h-4.85v-1.3h4.85v-4.85h1.3v4.85h4.85z" />
          </svg>
        </S.FullWidthButton>
      </Box>
      <S.Wrapper isOpen={isOpen}>
        <Box display={{ _: 'block', large: 'none' }} px="10px">
          <S.FullWidthButton onClick={onClose} isOpen={isOpen}>
            <svg height="15" width="15" viewBox="0 0 15 15">
              <path d="m8.9 7.5 6.1 6.1-1.4 1.4-6.1-6.1-6.1 6.1-1.4-1.4 6.1-6.1-6.1-6.1 1.4-1.4 6.1 6.1 6.1-6.1 1.4 1.4z" />
            </svg>
          </S.FullWidthButton>
        </Box>
        <S.Content ref={ref}>
          <Heading
            variant="pushTitle"
            as="h3"
            mb={[rem(50), 0]}
            display={{ _: 'block', large: 'none' }}
          >
            {messages.rolex.filters.filterYour}
          </Heading>
          <Box pl={{ large: '2rem' }}>{children}</Box>
        </S.Content>
        <S.Footer>
          <Button variant="primary" onClick={onConfirm}>
            {messages.rolex.filters.viewResults}
          </Button>
          <S.ResetButton variant="secondary" onClick={onReset} disabled={disableReset}>
            <svg height="15" width="15" viewBox="0 0 15 15" fill="currentColor">
              <path d="m15 7.5c0 3.9-3.1 7-7 7-2 0-3.8-.9-5.1-2.2l1.2-1.6c.9 1.1 2.3 1.8 3.9 1.8 2.8 0 5-2.2 5-5s-2.2-5-5-5c-2.6 0-4.7 2-4.9 4.5h.9l-1 1-1 1-1-1-1-1h1c0-.2 0-.4.1-.7 0-.2.1-.4.1-.6 0 0 0 0 0-.1 0-.2.1-.4.2-.5v-.1c.1-.2.1-.3.2-.5 0-.1 0-.1.1-.1.1-.2.2-.3.3-.4 0-.1 0-.2.1-.2.1-.1.2-.2.2-.3.1-.1.1-.1.2-.2 0-.2.1-.3.2-.4s.1-.2.2-.2c.1-.1.2-.2.2-.2.1-.1.2-.2.3-.2.1-.1.1-.2.2-.3.1 0 .2-.1.3-.2.1 0 .2-.1.3-.1.1-.1.2-.1.4-.2.1 0 .1-.1.2-.1 0-.1.2-.2.3-.3.1 0 .1-.1.2-.1.2 0 .3-.1.4-.1s.1-.1.2-.1.3-.1.4-.1.1 0 .2-.1c.2 0 .3-.1.5-.1h.2.7c4 0 7.1 3.1 7.1 7z" />
            </svg>
          </S.ResetButton>
        </S.Footer>
      </S.Wrapper>
    </>
  )
}

export default Sidebar
